import { GroupBase } from 'react-select';

import { Option } from '@/react/components/form-components/PortainerSelect';
import { RegistryValue } from '@/react/kubernetes/helm/components/HelmRegistrySelect';
import { HelmRelease } from '@/react/kubernetes/helm/types';

export const MSG_CANT_DETECT_SOURCE =
  "Portainer can't detect the Helm chart source for this release. Please set it to edit/upgrade the release.";

export const MSG_CANT_DETECT_PATH =
  "Portainer can't detect the Helm chart path for this release. Please set it to edit/upgrade the release.";

export const MSG_REGISTRY_NOT_SAVED =
  "Portainer found a registry for this release, but it isn't available in this namespace or has been removed from Portainer. Please add the registry or give the namespace access to it.";

function isBlank(value?: string) {
  return !value || !value.trim();
}

export interface ChartReferenceDetectionResult {
  selectedRegistry?: RegistryValue;
  selectedChartName?: string;
  infoAlert?: string;
  requiresSourceConfirmation: boolean;
  requiresChartConfirmation: boolean;
}

interface DetectionInput {
  release: HelmRelease;
  registryOptions: GroupBase<Option<RegistryValue>>[];
}

/**
 * Detects chart reference from a Helm release against available registry options.
 * Prioritizes registry-id over repo-url, validates against registryOptions,
 * and sets confirmation requirements based on detected source validity.
 */
export function detectChartReference({
  release,
  registryOptions,
}: DetectionInput): ChartReferenceDetectionResult {
  const { chartReference } = release;
  // No chart reference at all
  if (!chartReference) {
    return createDetectionResult({ infoAlert: MSG_CANT_DETECT_SOURCE });
  }

  const { chartPath, repoURL, registryID } = chartReference;

  // Use registry-id when present before checking repo (registry wins over repo)
  if (registryID) {
    return detectRegistryBasedChartReference(
      registryID,
      chartPath,
      registryOptions
    );
  }

  // Use repo-url when registry not present
  if (repoURL) {
    return detectRepositoryBasedChartReference(
      repoURL,
      chartPath,
      registryOptions
    );
  }

  // Check if we have any chart path but no valid source
  if (!isBlank(chartPath)) {
    return createDetectionResult({ infoAlert: MSG_CANT_DETECT_SOURCE });
  }

  // No valid chart source detected at all
  return createDetectionResult({ infoAlert: MSG_CANT_DETECT_SOURCE });
}

function createDetectionResult(
  overrides: Partial<ChartReferenceDetectionResult> = {}
): ChartReferenceDetectionResult {
  return {
    requiresSourceConfirmation: false,
    requiresChartConfirmation: false,
    ...overrides,
  };
}

/**
 * Handles detection for registry-based chart references
 */
function detectRegistryBasedChartReference(
  registryID: number,
  chartPath: string | undefined,
  registryOptions: GroupBase<Option<RegistryValue>>[]
): ChartReferenceDetectionResult {
  const detectedRegistry = findRegistryById(registryID, registryOptions);

  if (!detectedRegistry) {
    return createDetectionResult({
      infoAlert: MSG_REGISTRY_NOT_SAVED,
    });
  }

  // If chartPath is provided, assume confirmation is required; chart validation handled by hooks
  if (chartPath && chartPath.trim()) {
    const chartName = stripProjectPathPrefix(
      chartPath,
      detectedRegistry.projectPath
    );
    return createDetectionResult({
      selectedRegistry: detectedRegistry,
      selectedChartName: chartName,
      requiresSourceConfirmation: true,
      requiresChartConfirmation: true,
      infoAlert: '',
    });
  }

  return createDetectionResult({
    selectedRegistry: detectedRegistry,
    requiresSourceConfirmation: true,
    infoAlert: MSG_CANT_DETECT_PATH,
  });
}

/**
 * Handles detection for repository-based chart references
 */
function detectRepositoryBasedChartReference(
  repoURL: string,
  chartPath: string | undefined,
  registryOptions: GroupBase<Option<RegistryValue>>[]
): ChartReferenceDetectionResult {
  // First check if the repo URL is saved in registry options
  const savedRegistry = findRepoByUrl(repoURL, registryOptions);

  // Create a registry value - either from saved registry or temporary one
  const registryValue = savedRegistry || {
    registryUrl: repoURL,
    isOCI: false,
    name: repoURL,
  };

  // If chartPath is provided, assume confirmation is required; chart validation handled by hooks
  if (!isBlank(chartPath)) {
    return createDetectionResult({
      selectedRegistry: registryValue,
      selectedChartName: chartPath,
      requiresSourceConfirmation: true,
      requiresChartConfirmation: true,
      infoAlert: '',
    });
  }

  return createDetectionResult({
    selectedRegistry: registryValue,
    requiresSourceConfirmation: true,
    infoAlert: MSG_CANT_DETECT_PATH,
  });
}

/**
 * Finds a registry by ID in the registry options
 */
export function findRegistryById(
  registryId: number,
  registryOptions: GroupBase<Option<RegistryValue>>[]
): RegistryValue | undefined {
  const option = registryOptions
    .flatMap((group) => group.options)
    .find((option) => option.value.registryId === registryId);

  if (!option) {
    return undefined;
  }

  return {
    ...option?.value,
    // intentionally undefined, to not query with the repo url
    registryUrl: undefined,
  };
}

/** Gitlab is special and uses a project prefix for the chart path. */
function stripProjectPathPrefix(
  chartPath: string,
  projectPath?: string
): string {
  if (!projectPath) {
    return chartPath;
  }
  if (chartPath.startsWith(projectPath)) {
    return chartPath.slice(projectPath.length + 1); // +1 to remove the slash
  }
  return chartPath;
}

/**
 * Finds a registry by URL in the registry options
 */
export function findRepoByUrl(
  repoUrl: string,
  registryOptions: GroupBase<Option<RegistryValue>>[]
) {
  const option = registryOptions
    .flatMap((group) => group.options)
    .find((option) => option.value.registryUrl === repoUrl);
  return option?.value;
}
